import axios from 'axios';
import { tokenStorage } from '../utils/tokenStorage';
import { getOrCreateDeviceId } from '../utils/device';

const API_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';

const getAuthHeaders = () => ({
  headers: {
    Authorization: `Bearer ${tokenStorage.getAccessToken()}`,
    'X-Device-Id': getOrCreateDeviceId()
  }
});

export const attendanceService = {
  // HR Attendance APIs

  /**
   * Punch in for HR employee
   */
  punchIn: async () => {
    const response = await axios.post(
      `${API_URL}/hr/attendance/punch-in`,
      {},
      getAuthHeaders()
    );
    return response.data;
  },

  /**
   * Punch out for HR employee
   */
  punchOut: async () => {
    const response = await axios.post(
      `${API_URL}/hr/attendance/punch-out`,
      {},
      getAuthHeaders()
    );
    return response.data;
  },

  /**
   * Get HR employee's own attendance
   */
  getMyAttendance: async (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.month) queryParams.append('month', params.month);
    if (params.year) queryParams.append('year', params.year);
    if (params.limit) queryParams.append('limit', params.limit);

    const response = await axios.get(
      `${API_URL}/hr/attendance/my?${queryParams.toString()}`,
      getAuthHeaders()
    );
    return response.data;
  },

  /**
   * Get HR monthly expected/worked hours summary
   */
  getMyMonthlySummary: async ({ month, year }) => {
    const queryParams = new URLSearchParams();
    if (month) queryParams.append('month', month);
    if (year) queryParams.append('year', year);

    const response = await axios.get(
      `${API_URL}/hr/attendance/monthly-summary?${queryParams.toString()}`,
      getAuthHeaders()
    );
    return response.data;
  },

  /**
   * Get combined attendance and leave data for calendar
   */
  getMyAttendanceWithLeaves: async (params = {}) => {
    try {
      // Fetch both attendance and leave data
      const [attendanceResponse, leaveResponse] = await Promise.all([
        axios.get(
          `${API_URL}/hr/attendance/my?${new URLSearchParams(params).toString()}`,
          getAuthHeaders()
        ),
        axios.get(
          `${API_URL}/leave/my-leaves?${new URLSearchParams(params).toString()}`,
          getAuthHeaders()
        )
      ]);

      const attendanceData = attendanceResponse.data;
      const leaveData = leaveResponse.data;

      // Merge leave data into attendance records
      const mergedRecords = [...(attendanceData.data?.records || [])];

      // Add approved leave dates as attendance records
      if (leaveData.success && leaveData.data) {
        leaveData.data.forEach(leave => {
          if (leave.status === 'APPROVED') {
            const startDate = new Date(leave.startDate);
            const endDate = leave.endDate ? new Date(leave.endDate) : startDate;
            const leaveTypeName = leave.leaveTypeId?.name || leave.leaveTypeName || 'Leave';

            // Create leave records for each day
            for (let date = new Date(startDate); date <= endDate; date.setDate(date.getDate() + 1)) {
              const dateStr = date.toISOString().split('T')[0];

              // Check if there's already an attendance record for this date
              const existingRecord = mergedRecords.find(record =>
                record.date === dateStr ||
                new Date(record.date).toISOString().split('T')[0] === dateStr
              );

              if (!existingRecord) {
                mergedRecords.push({
                  date: dateStr,
                  status: leave.durationType === 'HALF_DAY' ? 'HalfDayLeave' : 'OnLeave',
                  punchIn: null,
                  punchOut: null,
                  totalHours: leave.durationType === 'HALF_DAY' ? 4 : 8,
                  leaveType: leave.durationType,
                  leaveTypeName,
                  leaveReason: leave.reason,
                  isLeave: true
                });
              }
            }
          }
        });
      }

      // Sort records by date
      mergedRecords.sort((a, b) => new Date(a.date) - new Date(b.date));

      return {
        success: true,
        data: {
          todayAttendance: attendanceData.data?.todayAttendance || null,
          records: mergedRecords,
          leaveData: leaveData.data || []
        }
      };
    } catch (error) {
      console.error('Error fetching attendance with leaves:', error);
      // Fallback to just attendance data
      const response = await axios.get(
        `${API_URL}/hr/attendance/my?${new URLSearchParams(params).toString()}`,
        getAuthHeaders()
      );
      return response.data;
    }
  },

  // HRO Attendance APIs

  /**
   * Get all HR employees' attendance (HRO only)
   */
  getAllHRAttendance: async (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.date) queryParams.append('date', params.date);
    if (params.month) queryParams.append('month', params.month);
    if (params.year) queryParams.append('year', params.year);
    if (params.hrId) queryParams.append('hrId', params.hrId);
    if (params.status) queryParams.append('status', params.status);
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);

    const response = await axios.get(
      `${API_URL}/hro/attendance/all?${queryParams.toString()}`,
      getAuthHeaders()
    );
    return response.data;
  },

  /**
   * Get all HR attendance with leave data (HRO only)
   */
  getAllHRAttendanceWithLeaves: async (params = {}) => {
    try {
      const parseSupportedDate = (value) => {
        if (!value) return null;

        if (value instanceof Date) {
          return Number.isNaN(value.getTime()) ? null : value;
        }

        if (typeof value !== 'string') {
          return null;
        }

        const trimmed = value.trim();
        if (!trimmed) {
          return null;
        }

        if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
          const parsed = new Date(`${trimmed}T00:00:00.000Z`);
          return Number.isNaN(parsed.getTime()) ? null : parsed;
        }

        if (/^\d{2}-\d{2}-\d{4}$/.test(trimmed)) {
          const [day, month, year] = trimmed.split('-').map(Number);
          const parsed = new Date(Date.UTC(year, month - 1, day, 0, 0, 0, 0));
          if (
            parsed.getUTCFullYear() !== year
            || parsed.getUTCMonth() !== month - 1
            || parsed.getUTCDate() !== day
          ) {
            return null;
          }
          return parsed;
        }

        const parsed = new Date(trimmed);
        return Number.isNaN(parsed.getTime()) ? null : parsed;
      };

      const normalizeDate = (value) => {
        const parsed = parseSupportedDate(value);
        return parsed ? parsed.toISOString().split('T')[0] : '';
      };

      const getDateRange = (queryParams) => {
        if (queryParams.date) {
          const selectedDate = parseSupportedDate(queryParams.date);
          if (!selectedDate) {
            return null;
          }
          selectedDate.setUTCHours(0, 0, 0, 0);
          return {
            start: new Date(selectedDate),
            end: new Date(selectedDate)
          };
        }

        if (queryParams.month && queryParams.year) {
          const month = parseInt(queryParams.month, 10);
          const year = parseInt(queryParams.year, 10);

          if (Number.isNaN(month) || Number.isNaN(year)) {
            return null;
          }

          const start = new Date(Date.UTC(year, month - 1, 1, 0, 0, 0, 0));
          const end = new Date(Date.UTC(year, month, 0, 0, 0, 0, 0));

          const today = new Date();
          today.setUTCHours(0, 0, 0, 0);

          // Avoid marking future dates as absent.
          if (end > today) {
            end.setTime(today.getTime());
          }

          if (start > end) {
            return null;
          }

          return { start, end };
        }

        return null;
      };

      // Fetch full filtered attendance set once, then paginate locally after leave merge.
      const attendanceParams = { ...params, page: 1, limit: 10000 };

      // Prepare params for leave API (it uses userId instead of hrId and startDate/endDate instead of month/year)
      const leaveParams = { ...params };
      if (params.hrId) {
        leaveParams.userId = params.hrId;
        delete leaveParams.hrId;
      }

      // Date filter takes precedence over month/year for leave API
      if (params.date) {
        leaveParams.startDate = params.date;
        leaveParams.endDate = params.date;
        delete leaveParams.month;
        delete leaveParams.year;
      } else if (params.month && params.year) {
        // Convert month/year to startDate/endDate for leave API
        const month = parseInt(params.month);
        const year = parseInt(params.year);
        leaveParams.startDate = `${year}-${month.toString().padStart(2, '0')}-01`;
        const lastDay = new Date(year, month, 0).getDate();
        leaveParams.endDate = `${year}-${month.toString().padStart(2, '0')}-${lastDay.toString().padStart(2, '0')}`;
        delete leaveParams.month;
        delete leaveParams.year;
      }

      // Remove pagination params from leave API call
      delete leaveParams.page;
      delete leaveParams.limit;

      // Fetch both attendance and leave data
      const [attendanceResponse, leaveResponse, hrListResponse] = await Promise.all([
        axios.get(
          `${API_URL}/hro/attendance/all?${new URLSearchParams(attendanceParams).toString()}`,
          getAuthHeaders()
        ),
        axios.get(
          `${API_URL}/leave/all?${new URLSearchParams(leaveParams).toString()}`,
          getAuthHeaders()
        ),
        axios.get(
          `${API_URL}/hro/attendance/hr-list`,
          getAuthHeaders()
        )
      ]);

      const attendanceData = attendanceResponse.data;
      const leaveData = leaveResponse.data;

      // Merge leave data into attendance records
      const mergedRecords = [...(attendanceData.data?.records || [])];

      // Add approved leave dates as attendance records
      if (leaveData.success && leaveData.data) {
        leaveData.data.forEach(leave => {
          if (leave.status === 'APPROVED') {
            const startDate = new Date(leave.startDate);
            const endDate = leave.endDate ? new Date(leave.endDate) : startDate;
            const leaveTypeName = leave.leaveTypeId?.name || leave.leaveTypeName || 'Leave';

            // Create leave records for each day
            for (let date = new Date(startDate); date <= endDate; date.setDate(date.getDate() + 1)) {
              const dateStr = date.toISOString().split('T')[0];

              // Check if there's already an attendance record for this date and user
              const existingRecord = mergedRecords.find(record =>
                (normalizeDate(record.date) === dateStr) &&
                (record.hrId?._id || record.hrId)?.toString() === (leave.userId?._id || leave.userId)?.toString()
              );

              if (!existingRecord) {
                mergedRecords.push({
                  _id: `leave_${leave._id}_${dateStr}`,
                  date: dateStr,
                  hrId: leave.userId,
                  status: leave.durationType === 'HALF_DAY' ? 'HalfDayLeave' : 'OnLeave',
                  punchIn: null,
                  punchOut: null,
                  totalHours: leave.durationType === 'HALF_DAY' ? 4 : 8,
                  leaveType: leave.durationType,
                  leaveTypeName,
                  leaveReason: leave.reason,
                  isLeave: true
                });
              }
            }
          }
        });
      }

      // Add missing weekdays as absent for the selected range.
      const dateRange = getDateRange(params);
      const allHREmployees = hrListResponse?.data?.data || [];
      const targetEmployees = params.hrId
        ? allHREmployees.filter(employee => employee._id?.toString() === params.hrId.toString())
        : allHREmployees;

      if (dateRange && targetEmployees.length > 0) {
        const existingByEmployeeDate = new Set(
          mergedRecords.map(record => {
            const recordHrId = (record.hrId?._id || record.hrId || '').toString();
            return `${recordHrId}:${normalizeDate(record.date)}`;
          })
        );

        const cursor = new Date(dateRange.start.getTime());
        while (cursor <= dateRange.end) {
          // Sunday is treated as weekly off, so it should not be marked as absent.
          if (cursor.getUTCDay() !== 0) {
            const dateStr = normalizeDate(cursor);

            targetEmployees.forEach(employee => {
              const employeeId = employee._id?.toString();
              if (!employeeId) return;

              const key = `${employeeId}:${dateStr}`;
              if (!existingByEmployeeDate.has(key)) {
                mergedRecords.push({
                  _id: `absent_${employeeId}_${dateStr}`,
                  date: dateStr,
                  hrId: employee,
                  status: 'Absent',
                  punchIn: null,
                  punchOut: null,
                  totalHours: 0,
                  isAbsent: true
                });
                existingByEmployeeDate.add(key);
              }
            });
          }

          cursor.setUTCDate(cursor.getUTCDate() + 1);
        }
      }

      // Re-apply filters after merge so leave rows follow the same filter rules as attendance rows.
      let filteredRecords = mergedRecords;

      if (params.hrId) {
        filteredRecords = filteredRecords.filter(record => {
          const recordHrId = record.hrId?._id || record.hrId;
          return recordHrId?.toString() === params.hrId.toString();
        });
      }

      if (params.date) {
        const selectedDate = normalizeDate(params.date);
        filteredRecords = filteredRecords.filter(record => normalizeDate(record.date) === selectedDate);
      } else {
        if (params.month) {
          const selectedMonth = parseInt(params.month, 10);
          filteredRecords = filteredRecords.filter(record => {
            const recordDate = new Date(record.date);
            return (recordDate.getMonth() + 1) === selectedMonth;
          });
        }

        if (params.year) {
          const selectedYear = parseInt(params.year, 10);
          filteredRecords = filteredRecords.filter(record => {
            const recordDate = new Date(record.date);
            return recordDate.getFullYear() === selectedYear;
          });
        }
      }

      // Sort records by date (newest first)
      filteredRecords.sort((a, b) => new Date(b.date) - new Date(a.date));

      const paginationFromApi = attendanceData.data?.pagination || {};
      const safeLimit = parseInt(params.limit, 10) || paginationFromApi.limit || 20;
      const currentPage = parseInt(params.page, 10) || paginationFromApi.page || 1;
      const total = filteredRecords.length;
      const pages = Math.max(1, Math.ceil(total / safeLimit));
      const page = Math.min(currentPage, pages);
      const startIndex = (page - 1) * safeLimit;
      const paginatedRecords = filteredRecords.slice(startIndex, startIndex + safeLimit);

      const statusCounts = filteredRecords.reduce((acc, record) => {
        const status = record.status || 'Unknown';
        acc[status] = (acc[status] || 0) + 1;
        return acc;
      }, {});

      const statusSummary = Object.entries(statusCounts).map(([status, count]) => ({
        _id: status,
        count
      }));

      return {
        success: true,
        data: {
          records: paginatedRecords,
          statusSummary,
          pagination: {
            page,
            limit: safeLimit,
            total,
            pages
          },
          leaveData: leaveData.data || []
        }
      };
    } catch (error) {
      console.error('Error fetching attendance with leaves:', error);
      // Fallback to just attendance data
      const response = await axios.get(
        `${API_URL}/hro/attendance/all?${new URLSearchParams(params).toString()}`,
        getAuthHeaders()
      );
      return response.data;
    }
  },

  /**
   * Get monthly attendance summary (HRO only)
   */
  getMonthlyAttendanceSummary: async (year, month) => {
    const response = await axios.get(
      `${API_URL}/hro/attendance/monthly?year=${year}&month=${month}`,
      getAuthHeaders()
    );
    return response.data;
  },

  /**
   * Get HR employees list (HRO only)
   */
  getHREmployeesList: async () => {
    const response = await axios.get(
      `${API_URL}/hro/attendance/hr-list`,
      getAuthHeaders()
    );
    return response.data;
  },

  exportAttendanceToExcel: async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.date) params.append('date', filters.date);
    if (filters.month) params.append('month', filters.month);
    if (filters.year) params.append('year', filters.year);
    if (filters.hrId) params.append('hrId', filters.hrId);

    const response = await axios.get(
      `${API_URL}/hro/attendance/export?${params.toString()}`,
      {
        ...getAuthHeaders(),
        responseType: 'blob'
      }
    );

    // Create download link
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `attendance_${filters.year || 'all'}_${filters.month || 'all'}_${Date.now()}.xlsx`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);

    return { success: true };
  }
};

export default attendanceService;